
import React from 'react';
import styles from './_finance-taxes-editor.module.scss';
import {FinanceLoanClassFinanceTax, FinanceQuoteTax, GenericGrid} from "@amsanalytics/react-shared-library";
import { hasValue } from "@amsanalytics/react-shared-library";
import {TileLayout, TileLayoutItem, TilePosition} from "@progress/kendo-react-layout";
import {
  FinanceQuoteTaxWithAmounts,
  FinanceQuoteValues,
  mapTaxAmountsToFinanceQuoteTaxes
} from "../../../../utils/LoanCalcUtils";
import GenericItemEditorDialog, {
  GenericEditorActionType,
  IGenericItemEditorError,
  IGenericItemFieldInfo
} from "../../GenericItemEditorDialog/GenericItemEditorDialog";

export interface FinanceTaxesEditorProps {
  applicationQuoteTaxes: FinanceQuoteTax[];
  financeLoanClassFinanceTaxes: FinanceLoanClassFinanceTax[];
  onChange: (tax: FinanceQuoteTax) => void;
  financeQuoteFieldIsDisabled: (fieldName: string) => boolean;
  financeQuoteValues?: FinanceQuoteValues;
  // Dialog functions (passed from parent)
  openDialog: (options: { children: React.ReactNode }) => void;
  closeDialog: () => void;
}

export default function FinanceTaxesEditor(props:  FinanceTaxesEditorProps) {
  const { openDialog, closeDialog } = props;

  const editableFieldInfos: IGenericItemFieldInfo[] = [
    {name: "description", label: "Description", type: "string", required: true, readonly: true},
    {name: "rate", label: "Rate", type: "number", required: true, readonly: false}
  ];
  
  const validateTax = (tax: FinanceQuoteTax, actionType: GenericEditorActionType): IGenericItemEditorError[] => {
    const errors: IGenericItemEditorError[] = [];

    if (hasValue(tax.rate) && tax.rate! < 0) {
      errors.push({message: "The rate can't be less than 0", canBeBypassed: false});
    }

    return errors;
  }
  
  const editTax = (tax: FinanceQuoteTax) => {
    const buttonText = "Update";
    
    openDialog({
      children: (
        <GenericItemEditorDialog
          item={tax as FinanceQuoteTax}
          objectSpecificValidator={validateTax}
          title={"Tax"}
          editableFieldInfos={editableFieldInfos}
          submitButtonText={buttonText}
          onSubmit={(updatedTax: FinanceQuoteTax) => props.onChange(updatedTax)}
          closeDialog={closeDialog}
        />
      )
    })
  }
  
  // Maps the application quote and their rates to their actual dollar amount to display in each row
  const financeQuoteTaxWithAmounts = mapTaxAmountsToFinanceQuoteTaxes(props.applicationQuoteTaxes, props.financeLoanClassFinanceTaxes);

  const [data, setData] = React.useState<Array<TilePosition>>([
    { col: 1, colSpan: 12, rowSpan: 1, row: 1 }
  ]);

  const tiles: TileLayoutItem[] = [
    {
      defaultPosition: data[0],
      reorderable: false,
      resizable: false,
      header: 'Taxes',
      body: <div className={styles.financeTaxesEditorContainer}>
        <p style={{margin: '0 0 8px 0', fontSize: '12px', color: '#666', fontStyle: 'italic'}}>Dollar amounts are Carleton calculated amounts from the last solved quote.</p>
        <GenericGrid<FinanceQuoteTaxWithAmounts>
          style={{display: 'flex', flex: 1}}
          columns={[
            {title: "Description", field: "description"},
            {title: "Rate", field: "rate", format: '{0}'},
            {title: "Dollar Amount", field: "dollarAmount"}
          ]}
          itemName={'Tax'}
          items={financeQuoteTaxWithAmounts.sort((a, b) =>
            a.description > b.description ? 1 : -1
          ) ?? []}
          disableAdding={true}
          disableEditing={props.financeQuoteFieldIsDisabled("taxes")}
          disableExcelExporting={true}
          disablePdfExporting={true}
          disableFiltering={true}
          disableGrouping={true}
          onItemSelected={(tax: FinanceQuoteTaxWithAmounts) => { if (!props.financeQuoteFieldIsDisabled("taxes")) editTax(tax); }}
          disablePaging={financeQuoteTaxWithAmounts.length <= 20}
        />
      </div>,
    }
  ];

  return (
    <TileLayout
      columns={12}
      className={styles.quoteEditorTileLayout}
      gap={{rows: 10, columns: 10}}
      rowHeight={"100%"}
      // style={{display: 'flex', flex: 1}}
      style={{width: '100%'}}
      positions={data}
      items={tiles}
    />
  );
}
